import React, { useState, useEffect } from 'react';
import { BuildingOffice2Icon, UserGroupIcon, PlusCircleIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../util/auth';
import { useOrganisationsByOwner, createOrganisation, updateOrganisation } from '../../util/db';
import supabase from '../../util/supabase';



const systems = {
  ats: [
    { 
      id: 'ashby', 
      name: 'Ashby', 
      logo: 'https://logo.clearbit.com/ashbyhq.com',
      description: 'The all-in-one recruiting platform'
    },
    { 
      id: 'greenhouse', 
      name: 'Greenhouse', 
      logo: 'https://logo.clearbit.com/greenhouse.io',
      description: 'Your all-together hiring platform'
    }
  ],
  hris: [
    { 
      id: 'humaans', 
      name: 'Humaans', 
      logo: 'https://logo.clearbit.com/humaans.io',
      description: 'AI-Powered HRIS for high performing teams'
    },
    { 
      id: 'hibob', 
      name: 'HiBob', 
      logo: 'https://logo.clearbit.com/hibob.com',
      description: 'A modern HR software for the new world of work'
    }
  ]
};

export default function SystemSelectionForm({ onSubmit, onBack }) {
  const auth = useAuth();
  const ownerId = auth.user?.uid;
  const [existingOrganisation, setExistingOrganisation] = useState(null);
  
  const [formData, setFormData] = useState({
    ats: '',
    hris: '',
    other_ats: '',
    other_hris: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch existing organisation data
  const { data: organisations, error: organisationsError } = useOrganisationsByOwner(ownerId);

  // Set initial form data from existing organisation
  useEffect(() => {
    if (organisations) {
      const userOrganisation = organisations[0];
      setExistingOrganisation(userOrganisation);
      
      if (userOrganisation) {
        // Check if the ATS value matches any predefined systems
        const atsSystem = systems.ats.find(sys => sys.id === userOrganisation.ats);
        const hrisSystem = systems.hris.find(sys => sys.id === userOrganisation.hris);

        setFormData({
          ats: atsSystem ? userOrganisation.ats : '',
          hris: hrisSystem ? userOrganisation.hris : '',
          other_ats: !atsSystem ? userOrganisation.ats : '',
          other_hris: !hrisSystem ? userOrganisation.hris : ''
        });
      }
      setIsLoading(false);
    }
  }, [organisations]);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const organisationData = {
        ats: formData.other_ats || formData.ats,
        hris: formData.other_hris || formData.hris
      };

      if (existingOrganisation) {
        await updateOrganisation(existingOrganisation.id, organisationData);
      }

      onSubmit(organisationData);
    } catch (error) {
      console.error('Error submitting systems:', error);
      alert(`Failed to submit system selections: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const SystemCard = ({ system, type }) => {
    const isSelected = type === 'ats' ? 
      (formData.ats === system.id && !formData.other_ats) : 
      (formData.hris === system.id && !formData.other_hris);

    return (
      <div
        className={`
          w-full p-3 rounded-xl border-2 cursor-pointer
          transition-all duration-200
          ${isSelected 
            ? 'bg-gradient-to-r from-indigo-600 to-purple-600 border-transparent text-white' 
            : 'bg-white border-gray-200 hover:border-indigo-600'
          }
        `}
        onClick={() => {
          setFormData(prev => ({
            ...prev,
            [type]: system.id,
            [`other_${type}`]: ''
          }));
        }}
      >
        <div className="flex items-center">
          <img 
            src={system.logo} 
            alt={`${system.name} logo`}
            className="w-8 h-8 rounded-lg object-contain"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='%23CBD5E0' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='12' y1='8' x2='12' y2='16'%3E%3C/line%3E%3Cline x1='8' y1='12' x2='16' y2='12'%3E%3C/line%3E%3C/svg%3E";
            }}
          />
          <div className="ml-3">
            <h3 className="font-medium text-sm">{system.name}</h3>
            <p className={`text-xs ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
              {system.description}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const isValid = () => {
    return (formData.ats || formData.other_ats) && 
           (formData.hris || formData.other_hris);
  };

  // Update input handlers for "Other" fields
  const handleOtherInput = (type, value) => {
    setFormData(prev => ({
      ...prev,
      [type]: '',
      [`other_${type}`]: value
    }));
  };

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-[#667eea] animate-bounce" style={{ animationDelay: '0s' }} />
          <div className="w-4 h-4 rounded-full bg-[#667eea] animate-bounce" style={{ animationDelay: '0.2s' }} />
          <div className="w-4 h-4 rounded-full bg-[#667eea] animate-bounce" style={{ animationDelay: '0.4s' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4">
      {/* Welcome Section */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-50 mb-4">
          <ComputerDesktopIcon className="w-8 h-8 text-indigo-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          What's Your Tech Stack? 🔗
        </h1>
        <p className="text-gray-500 text-lg">
          Tell us about your HR tech
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <BuildingOffice2Icon className="h-5 w-5 text-indigo-600" />
            <h3 className="text-base font-semibold text-gray-900">Applicant Tracking System</h3>
          </div>
          <div className="space-y-3">
            {systems.ats.map(system => (
              <SystemCard key={system.id} system={system} type="ats" />
            ))}
            <div className="mt-4 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <PlusCircleIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={formData.other_ats}
                onChange={(e) => handleOtherInput('ats', e.target.value)}
                placeholder="Using a different ATS? Add it here"
                className="w-full pl-10 pr-4 py-3 text-sm rounded-xl border-2
                  bg-gray-50 border-gray-200
                  focus:ring-2 focus:ring-indigo-600 focus:border-transparent focus:bg-white
                  placeholder:text-gray-400 transition-all duration-200
                  hover:border-gray-300"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <UserGroupIcon className="h-5 w-5 text-indigo-600" />
            <h3 className="text-base font-semibold text-gray-900">HR Information System</h3>
          </div>
          <div className="space-y-3">
            {systems.hris.map(system => (
              <SystemCard key={system.id} system={system} type="hris" />
            ))}
            <div className="mt-4 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <PlusCircleIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={formData.other_hris}
                onChange={(e) => handleOtherInput('hris', e.target.value)}
                placeholder="Using a different HRIS? Add it here"
                className="w-full pl-10 pr-4 py-3 text-sm rounded-xl border-2
                  bg-gray-50 border-gray-200
                  focus:ring-2 focus:ring-indigo-600 focus:border-transparent focus:bg-white
                  placeholder:text-gray-400 transition-all duration-200
                  hover:border-gray-300"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <div className="flex justify-center space-x-4">
          <button
            type="button"
            onClick={onBack}
            className="back-button"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="submit-button"
            disabled={isLoading || !isValid()}
          >
            {isSubmitting ? "Saving..." : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
} 